import InputAdornment from '@mui/material/InputAdornment';
import NonNegativeNumberField from '../../../components/ui/forms/NonNegativeNumberField';
import { ZONE_DISTANCE_DECIMALS, ZONE_DISTANCE_MAX, ZONE_PRICE_DECIMALS, ZONE_PRICE_MAX, ZONE_SURGE_MULTIPLIER_DECIMALS, ZONE_SURGE_MULTIPLIER_MAX, ZONE_SURGE_MULTIPLIER_MIN } from '../../../constants/transportPartnerFlow';
import { getCurrencySymbol } from '../../../utils/currency';
import {
  validateZoneDistanceKm,
  validateZonePricingAmount,
  validateZoneWholeNumber,
  validateZoneDurationMinutes,
  validateSurgeMultiplier,
} from '../zoneTypes';

type BaseProps = {
  label: string;
  value: number;
  onValueChange: (n: number) => void;
};

type AmountProps = BaseProps & {
  currency?: string;
};

export function ZoneDistanceNumberField({ label, value, onValueChange }: BaseProps) {
  const error = validateZoneDistanceKm(value);
  return (
    <NonNegativeNumberField
      fullWidth
      size="small"
      label={label}
      value={value}
      max={ZONE_DISTANCE_MAX}
      decimalPlaces={ZONE_DISTANCE_DECIMALS}
      onValueChange={onValueChange}
      error={Boolean(error)}
      helperText={
        error
        || `Non-negative · max ${ZONE_DISTANCE_MAX.toLocaleString('en-IN')} · up to ${ZONE_DISTANCE_DECIMALS} decimal places`
      }
    />
  );
}

export function ZoneAmountNumberField({ label, value, onValueChange, currency }: AmountProps) {
  const error = validateZonePricingAmount(value);
  const symbol = getCurrencySymbol(currency);
  const currencyMark = symbol || (currency?.trim() ? currency.trim().toUpperCase() : '');
  return (
    <NonNegativeNumberField
      fullWidth
      size="small"
      label={label}
      value={value}
      max={ZONE_PRICE_MAX}
      decimalPlaces={ZONE_PRICE_DECIMALS}
      onValueChange={onValueChange}
      error={Boolean(error)}
      helperText={
        error
        || (currencyMark
          ? `Non-negative · max ${ZONE_PRICE_MAX.toLocaleString('en-IN')} · ${currencyMark} · up to ${ZONE_PRICE_DECIMALS} decimal places`
          : `Non-negative · max ${ZONE_PRICE_MAX.toLocaleString('en-IN')} · up to ${ZONE_PRICE_DECIMALS} decimal places`)
      }
      InputProps={
        currencyMark
          ? {
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {currencyMark}
              </InputAdornment>
            ),
          }
          : undefined
      }
      sx={{
        '& .MuiInputAdornment-root': { mr: 0.25 },
        '& .MuiInputBase-input': { color: 'text.primary' },
      }}
    />
  );
}

export function ZoneWholeNumberField({ label, value, onValueChange }: BaseProps) {
  const error = validateZoneWholeNumber(value);
  return (
    <NonNegativeNumberField
      fullWidth
      size="small"
      label={label}
      value={value}
      integer
      onValueChange={onValueChange}
      error={Boolean(error)}
      helperText={error || 'Whole number · 0 or greater'}
    />
  );
}

export function ZoneDurationMinutesField({ label, value, onValueChange }: BaseProps) {
  const error = validateZoneDurationMinutes(value);
  return (
    <NonNegativeNumberField
      fullWidth
      size="small"
      label={label}
      value={value}
      integer
      min={1}
      onValueChange={onValueChange}
      error={Boolean(error)}
      helperText={error || 'Whole number · 1 or greater'}
    />
  );
}

export function ZoneSurgeMultiplierField({ label, value, onValueChange }: BaseProps) {
  const error = validateSurgeMultiplier(value);
  return (
    <NonNegativeNumberField
      fullWidth
      size="small"
      label={label}
      value={value}
      max={ZONE_SURGE_MULTIPLIER_MAX}
      decimalPlaces={ZONE_SURGE_MULTIPLIER_DECIMALS}
      onValueChange={onValueChange}
      error={Boolean(error)}
      helperText={
        error
        || `${ZONE_SURGE_MULTIPLIER_MIN}–${ZONE_SURGE_MULTIPLIER_MAX} · up to ${ZONE_SURGE_MULTIPLIER_DECIMALS} decimal places (e.g. 1.3 = 30% surge)`
      }
    />
  );
}
